<template>
  <div class="step-container client">
    <v-form name="JobDescription" class="form" data-test-id="job-description-form">
      <v-container class="client-step-container">
        <syncing-indicator :pageName="MENUS.CLIENT.JOB_DESCRIPTION.id"></syncing-indicator>
        <v-row class="mb-8">
          <v-col class="instructions">
            <h1 data-test-id="job-description-header">
              <span class="headerlight">
                What best describes your <strong>job?</strong>
              </span>
            </h1>
          </v-col>
        </v-row>
        <data-submitted-message
          :submitted="isJobDescriptionComplete"
          v-on:hide-form="toggleFormState"
          data-test-id="data-submitted-message"
        />
        <div v-if="!formHidden && !isSyncing">
          <v-row class="justify-center">
            <v-col class="step-form-container radio-container mb-4">
              <v-radio-group
                v-model="jobDescription"
                :error-messages="jobDescriptionErrors"
                hide-details="auto"
                @change="$v.jobDescription.$touch()"
                data-test-id="job-description-radio-group"
              >
                <v-radio
                  :ripple="false"
                  class="v-faux-radio mb-4"
                  :label="JOB_DESCRIPTIONS.PROFESSIONAL"
                  :value="JOB_DESCRIPTIONS.PROFESSIONAL"
                  data-test-id="professional-radio"
                ></v-radio>
                <div class="expander" v-show="isProfessionalJobType">
                  <div class="faux-label" data-test-id="professional-job-type-label">Job Type</div>
                  <div>
                    <v-select
                      v-model="professionalJobType"
                      :items="jobTypes.professional"
                      :backgroundColor="inputBackgroundColor"
                      outlined
                      placeholder="Choose"
                      dense
                      hide-details="auto"
                      @change="$v.professionalJobType.$touch()"
                      :error-messages="professionalJobTypeErrors"
                      class="mb-2"
                      data-test-id="professional-job-type-select"
                    >
                    </v-select>
                  </div>
                </div>
                <v-radio
                  :ripple="false"
                  class="v-faux-radio"
                  :label="JOB_DESCRIPTIONS.TRADE_OR_SERVICE"
                  :value="JOB_DESCRIPTIONS.TRADE_OR_SERVICE"
                  data-test-id="trade-service-radio"
                ></v-radio>
                <v-radio
                  :ripple="false"
                  class="v-faux-radio mb-4"
                  :label="JOB_DESCRIPTIONS.GOVERNMENT"
                  :value="JOB_DESCRIPTIONS.GOVERNMENT"
                  data-test-id="government-radio"
                ></v-radio>
                <div class="expander" v-show="isGovernmentJobType">
                  <div class="faux-label" data-test-id="government-job-type-label">Job Type</div>
                  <div>
                    <v-select
                      v-model="governmentJobType"
                      :items="jobTypes.government"
                      :backgroundColor="inputBackgroundColor"
                      outlined
                      placeholder="Choose"
                      dense
                      hide-details="auto"
                      @change="$v.governmentJobType.$touch()"
                      :error-messages="governmentJobTypeErrors"
                      class="mb-2"
                      data-test-id="government-job-type-select"
                    >
                    </v-select>
                  </div>
                </div>
                <v-radio
                  :ripple="false"
                  class="v-faux-radio mb-4"
                  :label="JOB_DESCRIPTIONS.OTHER"
                  :value="JOB_DESCRIPTIONS.OTHER"
                  data-test-id="other-radio"
                ></v-radio>
                <div class="expander empty" v-show="isOtherJobType">
                  <label for="other" data-test-id="other-job-type-label">What's your job?</label>
                  <v-text-field
                    outlined
                    type="text"
                    maxlength="256"
                    name="other"
                    v-model.trim="otherJobType"
                    :backgroundColor="inputBackgroundColor"
                    @input="$v.otherJobType.$touch()"
                    @blur="$v.otherJobType.$touch()"
                    :error-messages="otherJobTypeErrors"
                    hide-details="auto"
                    data-test-id="other-job-type-input"
                  />
                </div>
              </v-radio-group>
            </v-col>
          </v-row>

          <div class="skip-for-now">
            <a @click="skip()" data-test-id="skip-button">Skip for now</a>
          </div>
        </div>
      </v-container>
    </v-form>
    <bottom-nav v-on:go-prev="prev()" v-on:go-next="next()" data-test-id="job-description-nav"></bottom-nav>
  </div>
</template>

<script>
import DataSubmittedMessage from '@/components/client/DataSubmittedMessage';
import BottomNav from '@/components/common/BottomNav';
import InterviewClientMixin from '@/common/mixins/interview.client.mixin';
import { required, requiredIf } from 'vuelidate/lib/validators';
import {
  MENUS,
  JOB_DESCRIPTIONS,
  JOB_TYPES,
  JOB_TYPE_TRADE,
} from '@/common/constants';
import SyncingIndicator from '@/components/common/SyncingIndicator.vue';

export default {
  name: 'JobDescription',

  components: { BottomNav, DataSubmittedMessage, SyncingIndicator },
  mixins: [InterviewClientMixin],

  data: () => ({
    jobDescription: null,
    professionalJobType: null,
    governmentJobType: null,
    otherJobType: null,
    JOB_DESCRIPTIONS: JOB_DESCRIPTIONS,
    JOB_TYPE_TRADE: JOB_TYPE_TRADE,
    MENUS: MENUS,
  }),

  validations() {
    return {
      jobDescription: {
        required,
      },
      professionalJobType: {
        required: requiredIf(() => this.isProfessionalJobType),
      },
      governmentJobType: {
        required: requiredIf(() => this.isGovernmentJobType),
      },
      otherJobType: {
        required: requiredIf(() => this.isOtherJobType),
      },
      validationGroup: [
        'jobDescription',
        'professionalJobType',
        'governmentJobType',
        'otherJobType',
      ],
    };
  },

  computed: {
    /* Dynamic errors */
    jobDescriptionErrors() {
      const errors = [];
      if (!this.$v.jobDescription.$dirty) return errors;
      !this.$v.jobDescription.required &&
        errors.push('A selection is required');
      return errors;
    },

    professionalJobTypeErrors() {
      const errors = [];
      if (!this.$v.professionalJobType.$dirty) return errors;
      !this.$v.professionalJobType.required &&
        errors.push('A selection is required');
      return errors;
    },

    governmentJobTypeErrors() {
      const errors = [];
      if (!this.$v.governmentJobType.$dirty) return errors;
      !this.$v.governmentJobType.required &&
        errors.push('A selection is required');
      return errors;
    },

    otherJobTypeErrors() {
      const errors = [];
      if (!this.$v.otherJobType.$dirty) return errors;
      !this.$v.otherJobType.required && errors.push('This field is required');
      return errors;
    },

    isProfessionalJobType() {
      return this.jobDescription === this.JOB_DESCRIPTIONS.PROFESSIONAL;
    },
    isGovernmentJobType() {
      return this.jobDescription === this.JOB_DESCRIPTIONS.GOVERNMENT;
    },
    isOtherJobType() {
      return this.jobDescription === this.JOB_DESCRIPTIONS.OTHER;
    },

    // Check if the page is currently syncing
    isSyncing() {
      const page = this.getPage(MENUS.CLIENT.JOB_DESCRIPTION.id);
      return page && page.status === 'IN_PROGRESS';
    },
  },

  methods: {
    /* Nav to Next Page in flow */
    next(route = null) {      
      let to = route || this.pages[this.pageIndex + 1];

      if (!this.formHidden) {
        this.$v.$touch();
        if (this.$v.$invalid) return;
      } else {
        this.goNext(to);
        return;
      }

      let type = null;
      switch (this.jobDescription) {
        case this.JOB_DESCRIPTIONS.PROFESSIONAL:
          type = this.professionalJobType;
          break;
        case this.JOB_DESCRIPTIONS.GOVERNMENT:
          type = this.governmentJobType;
          break;
        case this.JOB_DESCRIPTIONS.OTHER:
          type = this.otherJobType;
          break;
        case this.JOB_DESCRIPTIONS.TRADE_OR_SERVICE:
          type = this.JOB_TYPE_TRADE;
      }

      const data = {
        description: this.jobDescription,
        type: type,
      };

      // Mark page as syncing immediately for better user feedback
      this.markPageAsSyncing(this.pages[this.pageIndex]);

      this.completeStep(this.pages[this.pageIndex], data)
        .then(() => {
          this.goNext(to);
        })
        .catch(() => {});
    },

    /* Nav to Previous Page in flow */
    prev() {
      this.goPrev(this.pages[this.pageIndex - 1]);
    },

    /* Skip to next Page in flow */
    skip() {
      // Fetch updated interview progress before navigation
      this.$store.dispatch('GET_INTERVIEW_PROGRESS').finally(() => {        
        this.goNext(this.pages[this.pageIndex + 1]);
      });
    },
  },

  created() {
    this.interviewPages.addEmploymentPages();
    this.pages = this.interviewPages.getInterviewPages();
    this.pageIndex = this.pages.indexOf(MENUS.CLIENT.JOB_DESCRIPTION.id);
    // Initialize form data for current interview
    this.formHidden = this.isJobDescriptionComplete;
    this.jobTypes = JOB_TYPES;
  },
};
</script>
<style lang="scss" scoped>
.expander {
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid var(--secondary-color);

  &.empty {
    border-bottom: none;
  }
}
</style>
